// The text side of search: one spelling for a word however it was typed, a
// light stem so a plural finds its singular, and how well one word matches
// another. Pure — no DOM, no storage — so it can be tested on its own.
//
// Reading a query (which words say what kind of thing, and when) is
// services/search-query.ts; the rows the words are run over are built in
// services/search.ts.

// Letters an Arabic keyboard writes where a Persian one writes another, so the
// same word from either keyboard is one word.
const LETTERS: Record<string, string> = {
    'ي': 'ی', 'ى': 'ی', 'ك': 'ک', 'ة': 'ه', 'ؤ': 'و', 'إ': 'ا', 'أ': 'ا', 'ٱ': 'ا',
}

// Diacritics, the tatweel and the hamza marks come off; digits in either
// script become Latin; everything that is not a Persian or Latin letter or a
// digit becomes a space — punctuation, and the half-space (ZWNJ) with it, so
// مناسبت‌ها and مناسبت ها and مناسبتها read the same.
export function normalize(text: string): string {
    return text
        .replace(/[ً-ٰٕـ]/g, '')
        .replace(/[يىكةؤإأٱ]/g, ch => LETTERS[ch])
        .replace(/[۰-۹]/g, d => String(d.charCodeAt(0) - 0x06F0))
        .replace(/[٠-٩]/g, d => String(d.charCodeAt(0) - 0x0660))
        .toLowerCase()
        .replace(/[^ؠ-يٱ-ەa-z0-9]+/g, ' ')
        .trim()
}

// A plural ending first, while at least three letters remain: کارهای → کار.
// Then an adjective or plural one, while at least four remain: نویسان → نویس,
// تعطیلی → تعطیل. Four, because ان and ی end many words that are not plurals
// or adjectives at all — مامان, ایران, آسمان, ماهی — and three letters is
// where those would be cut down to other words. Both sides of a match are
// stemmed the same way, so a stem that is not a real word still matches itself.
const PLURAL = ['هایی', 'های', 'ها']
const ENDINGS = ['ترین', 'تر', 'ان', 'ی']

// The words of a text, normalized. A lone letter is noise — the ezafe ی that a
// half-space left standing — and so is a plural ending standing on its own
// after a space; but a lone digit is a day of the month.
export function words(text: string): string[] {
    return normalize(text).split(' ')
        .filter(t => (t.length > 1 || /\d/.test(t)) && !PLURAL.includes(t))
}

const strip = (word: string, suffixes: string[], left: number) => {
    for (const s of suffixes) {
        if (word.endsWith(s) && word.length - s.length >= left) return word.slice(0, -s.length)
    }
    return word
}

export const stem = (word: string) =>
    /^\d+$/.test(word) ? word : strip(strip(word, PLURAL, 3), ENDINGS, 4)

export const stems = (text: string) => words(text).map(stem)

// One letter changed, added or taken away.
function withinOne(a: string, b: string): boolean {
    if (a.length === b.length) {
        let diff = 0
        for (let i = 0; i < a.length; i++) {
            if (a[i] !== b[i] && ++diff > 1) return false
        }
        return true
    }
    const [short, long] = a.length < b.length ? [a, b] : [b, a]
    if (long.length - short.length !== 1) return false
    let i = 0
    let j = 0
    let skipped = false
    while (i < short.length) {
        if (short[i] === long[j]) {
            i++
            j++
        } else if (skipped) {
            return false
        } else {
            skipped = true
            j++
        }
    }
    return true
}

// How well a query word matches a row's word: the same word, the start of a
// longer one, a typo in a word long enough to survive one, or a part of one.
export function match(q: string, r: string): number {
    if (q === r) return 1
    if (q.length >= 2 && r.startsWith(q)) return 0.8
    if (q.length >= 5 && r.length >= 5 && withinOne(q, r)) return 0.6
    if (q.length >= 3 && r.includes(q)) return 0.5
    return 0
}

// What the ranking needs to know about a row: its words stemmed, the same
// words as written (for the word vectors), whether the reader wrote it, how
// many days it is from today (negative for past), and where its kind stands
// when nothing else tells two rows apart.
export interface IRankable {
    words: string[]
    terms: string[]
    own: boolean
    distance: number
    order: number
}

// How near a query word is in meaning to a row's word as written, from 0 to
// below a right spelling's 1. Takes the query word's position. Supplied by the
// word vectors once they are loaded — see services/search-vectors.ts.
export type Similar = (q: number, term: string) => number

export interface IRanked<T> {
    item: T
    score: number
}

// A word that turns up in hundreds of rows — روز — says almost nothing about
// which one is meant, so every query word is weighted by how few rows it
// touches. A word touching none is not in the search at all: it cannot help
// any row, so it must not count against them either.
const weightFor = (rows: number) => rows ? 1 / Math.log2(2 + rows) : 0

// The rows that answer the query, best first. A row answers when the words it
// matches carry at least half the query's weight; ties go to the nearer day,
// then to the future over the past, then to the kind's own order.
export function rank<T extends IRankable>(query: string[], items: T[], similar?: Similar): IRanked<T>[] {
    const best = items.map(item => query.map(q => {
        let b = 0
        for (const r of item.words) {
            b = Math.max(b, match(q, r))
            if (b === 1) break
        }
        return b
    }))
    // Meaning, for what spelling misses. What the reader wrote always gets
    // it: people write مامان where a title would say مادر, and an occasion that
    // happens to spell the word, روز جهانی زبان مادری, must not hide their
    // task. The calendar's own occasions get it only for a word no row spells
    // at all, so عید فطر lists عید فطر and not every feast near it in meaning.
    if (similar) {
        query.forEach((_, j) => {
            const spelled = best.some(b => b[j] > 0)
            items.forEach((item, i) => {
                if (spelled && !item.own) return
                for (const t of item.terms) best[i][j] = Math.max(best[i][j], similar(j, t))
            })
        })
    }
    const weight = query.map((_, i) => weightFor(best.filter(b => b[i] > 0).length))
    const total = weight.reduce((a, b) => a + b, 0)
    if (!total) return []

    const out: IRanked<T>[] = []
    items.forEach((item, i) => {
        const score = best[i].reduce((sum, b, j) => sum + weight[j] * b, 0) / total
        if (score >= 0.5) out.push({ item, score })
    })
    return out.sort((a, b) =>
        b.score - a.score
        || Math.abs(a.item.distance) - Math.abs(b.item.distance)
        || b.item.distance - a.item.distance
        || a.item.order - b.item.order)
}

// A listing over a window reads from its end back to its start: this year's
// holidays from اسفند back to فروردین, last week from Friday back to Saturday.
export const latestFirst = <T extends IRankable>(items: T[]) =>
    [...items].sort((a, b) => b.distance - a.distance || a.order - b.order)

// A listing with no window starts from today and works outward, the future
// before the past.
export const nearest = <T extends IRankable>(items: T[]) =>
    [...items].sort((a, b) =>
        Math.abs(a.distance) - Math.abs(b.distance)
        || b.distance - a.distance
        || a.order - b.order)
